import { FC, ReactNode } from "react";
import { IonIcon, IonSkeletonText, useIonRouter } from "@ionic/react";
import { giftOutline } from "ionicons/icons";
import { useTranslation } from "react-i18next";

import { routes } from "../../../route/constants";
import { BOARD_ROWS, initialsOf } from "../leaderboardModel";

const KEY = "leaderboards.VenuePage.";

export interface BoardRowProps {
    /** The rank column: a number, or a label like "100+" for a player past the stored cut. */
    rank: ReactNode;
    displayName: string;
    /** Already formatted for display; the unit is rendered smaller beside it. */
    value: ReactNode;
    unit?: string;
    you?: boolean;
    /** Gold avatar for the row that holds the record — the board's own first-place marker. */
    gold?: boolean;
    /** Overrides the avatar initials — used by the synthetic "you" row. */
    initialsFrom?: string | null;
    /** Makes the row tappable. Only ever set on a row of the player's own. */
    onOpen?: () => void;
    testId?: string;
}

/**
 * One row of any venue board (SCORZA-739 design handoff, reference/02).
 *
 * Shared by the Local Legend table and the three metric boards. They are the same row — rank,
 * avatar, name, value — and were drawn that way; keeping two copies is how the "you" highlight
 * ends up on one board and not the other.
 */
export const BoardRow: FC<BoardRowProps> = ({
    rank,
    displayName,
    value,
    unit,
    you,
    gold,
    initialsFrom,
    onOpen,
    testId,
}) => {
    const { t } = useTranslation();

    const body = (
        <>
            <span className="venue-legend-list-rank">{rank}</span>
            <span
                className={`venue-avatar${gold ? " venue-avatar-gold" : ""}`}
                aria-hidden="true"
            >
                {initialsOf(initialsFrom ?? displayName)}
            </span>
            <span className="venue-legend-list-name">
                {displayName}
                {you && ` (${t(KEY + "you")})`}
            </span>
            <span className="venue-legend-list-value">
                {value}
                {unit ? <small>{unit}</small> : null}
            </span>
        </>
    );

    const className = `venue-legend-list-row${you ? " venue-standing-me" : ""}`;

    // Only the player's own rows resolve to a match on this device, so only they are tappable
    // — a stranger's row has nothing to open.
    if (onOpen) {
        return (
            <button
                type="button"
                className={`${className} venue-row-tappable`}
                data-testid={testId}
                onClick={onOpen}
            >
                {body}
            </button>
        );
    }

    return (
        <div className={className} data-testid={testId}>
            {body}
        </div>
    );
};

/** The `•••` break shown before a pinned "you" row that is not adjacent to the rows above it. */
export const BoardGap: FC = () => <div className="venue-legend-gap">•••</div>;

/**
 * The seats a board has not filled yet.
 *
 * A ground with one player on it is the normal early state, not an error, and an almost-blank
 * list read as one — so the board always shows its five places and says which are still open.
 * The skeletons are deliberately unanimated: these are empty seats, not content still loading,
 * and a shimmer would promise names that are not coming.
 *
 * The last open seat is the invitation instead, because the honest answer to an empty board is
 * that the people to fill it are the player's own team-mates.
 */
export const BoardRowPlaceholders: FC<{ filled: number }> = ({ filled }) => {
    const { t } = useTranslation();
    const router = useIonRouter();

    if (filled >= BOARD_ROWS) {
        return null;
    }

    const empty = BOARD_ROWS - filled;

    return (
        <>
            {Array.from({ length: empty - 1 }, (_, index) => (
                <div
                    key={`placeholder-${filled + index + 1}`}
                    className="venue-legend-list-row venue-legend-list-row-empty"
                    data-testid="venue-board-placeholder"
                    aria-hidden="true"
                >
                    <span className="venue-legend-list-rank">
                        {filled + index + 1}
                    </span>
                    <IonSkeletonText
                        animated={false}
                        className="venue-skeleton-avatar"
                    />
                    <span className="venue-legend-list-name">
                        <IonSkeletonText
                            animated={false}
                            className="venue-skeleton-name"
                        />
                    </span>
                    <IonSkeletonText
                        animated={false}
                        className="venue-skeleton-value"
                    />
                </div>
            ))}

            <button
                type="button"
                className="venue-legend-list-row venue-legend-list-row-invite"
                data-testid="venue-board-invite"
                onClick={() => router.push(routes.referFriend, "forward")}
            >
                <span className="venue-legend-list-rank" aria-hidden="true">
                    {BOARD_ROWS}
                </span>
                <span className="venue-invite-icon" aria-hidden="true">
                    <IonIcon icon={giftOutline} />
                </span>
                <span className="venue-legend-list-name">
                    {t(KEY + "inviteFriends")}
                </span>
                <span className="venue-legend-list-value" aria-hidden="true">
                    →
                </span>
            </button>
        </>
    );
};
